import React from "react";
import { Eye, SlidersHorizontal } from "lucide-react";
import { cn } from "@/lib/utils";
import SensitiveBadge from "@/main/react/sensitive/SensitiveBadge";
import { normalizeSensitivePreference, shouldMaskSensitiveMedia, type SensitivePreferenceMode } from "@/main/common/SensitivePreference";

type SensitiveMediaGuardVariant = "card" | "avatar" | "banner" | "inline" | "carousel";
type SensitiveMediaGuardMode = "overlay" | "replace";
type SensitiveMediaGuardOverlayStacking = "default" | "below-link-overlay";

/**
 * Where the HIDE mask's secondary route sends the viewer. Signed in, this is the one screen that can
 * change the preference back. Signed out it is authentication-gated, but not a dead end:
 * `CustomAuthenticationSuccessHandler` extends Spring's `SavedRequestAwareAuthenticationSuccessHandler`,
 * so `/login` returns the visitor here afterwards.
 */
const CONTENT_PREFERENCES_PATH = "/settings/content_preferences";

/**
 * The mask is the BLUR. Brightness is decoration, and until 2026-08-14 it was theme-blind: dark card
 * content is already near-black, so `brightness-50` halved what little structure survived the blur and
 * the whole card collapsed to a uniform `#060a16` against a `#030712` page — 1.02:1, indistinguishable
 * from a row that failed to render (`GROUP-7`). Dark therefore LIGHTENS instead of darkening; the
 * legibility loss still comes entirely from the blur, which is theme-independent.
 */
const variantContentClassName: Record<SensitiveMediaGuardVariant, string> = {
    card: "blur-xl brightness-50 dark:brightness-150 scale-[1.03]",
    avatar: "blur-md brightness-75 dark:brightness-140 scale-105",
    banner: "blur-lg brightness-60 dark:brightness-140 scale-[1.02]",
    inline: "blur-md brightness-75 dark:brightness-140",
    carousel: "blur-xl brightness-50 dark:brightness-150 scale-[1.03]",
};

export type SensitiveMediaGuardProps = {
    isSensitive?: boolean | null;
    isSpoiler?: boolean | null;
    hideSensitiveMedia?: boolean;
    sensitivePreference?: SensitivePreferenceMode;
    /**
     * The viewer owns this record. The sensitive flag is the AUTHOR'S OWN declaration about their own
     * work, so enforcing it against the author is not a safety behaviour: it left a member unable to
     * read or manage their own series on their own dashboard (`DASHBOARD-11`). Callers resolve this
     * from whatever they already hold — a DTO's `isViewerOwner`, a leader flag, or the logged-in
     * username — and it is never a reason to mask.
     */
    isViewerOwner?: boolean;
    initiallyRevealed?: boolean;
    variant?: SensitiveMediaGuardVariant;
    mode?: SensitiveMediaGuardMode;
    className?: string;
    contentClassName?: string;
    maskedContentClassName?: string;
    overlayClassName?: string;
    maskedContainerClassName?: string;
    overlayStacking?: SensitiveMediaGuardOverlayStacking;
    badgeLabel: React.ReactNode;
    label: React.ReactNode;
    helperText: React.ReactNode;
    revealText: React.ReactNode;
    /**
     * Label for the secondary route to the content-preference screen, shown on a card mask that came
     * from the viewer's own HIDE preference — under the reveal button, never instead of it (D6,
     * 2026-08-27: every masked card offers an in-place reveal, whatever the preference). Optional: a
     * caller that does not supply it just gets no route link.
     */
    preferencesRouteText?: React.ReactNode;
    revealButtonDataPlaywrightId?: string;
    onRevealChange?: (revealed: boolean) => void;
    children: React.ReactNode;
};

export default function SensitiveMediaGuard({
    isSensitive = false,
    isSpoiler = false,
    hideSensitiveMedia = true,
    sensitivePreference,
    isViewerOwner = false,
    initiallyRevealed = false,
    variant = "card",
    mode = "overlay",
    className,
    contentClassName,
    maskedContentClassName,
    overlayClassName,
    maskedContainerClassName,
    overlayStacking = "default",
    badgeLabel,
    label,
    helperText,
    revealText,
    preferencesRouteText,
    revealButtonDataPlaywrightId,
    onRevealChange,
    children,
}: SensitiveMediaGuardProps) {
    const [revealed, setRevealed] = React.useState(Boolean(initiallyRevealed));
    const isInlineCompact = variant === "inline";
    const isCardActionOverlay = variant === "card";

    React.useEffect(() => {
        setRevealed(Boolean(initiallyRevealed));
    }, [initiallyRevealed, isSensitive, isSpoiler, hideSensitiveMedia, sensitivePreference]);

    React.useEffect(() => {
        onRevealChange?.(revealed);
    }, [onRevealChange, revealed]);

    const effectivePreference = hideSensitiveMedia
        ? normalizeSensitivePreference(sensitivePreference, "blur")
        : "show";
    const maskedBySensitivePreference = shouldMaskSensitiveMedia(Boolean(isSensitive), effectivePreference);
    // Every masked region offers an in-place reveal, whatever the viewer's preference — D6, 2026-08-27
    // (the masking design doc §10), reversing the 2026-04-14 `allowsLocalReveal` gate that left HIDE
    // viewers (and every anonymous visitor, hard-defaulted to HIDE by `basic_layout.jte`) with no way
    // to uncover the blur in place. This flag marks the mask as HIDE-caused so the card panel can add
    // the content-preference route as a SECOND control beside the reveal, never as a substitute for it.
    const hidePreferenceMask = maskedBySensitivePreference && effectivePreference === "hide";
    // The owner exemption is applied here, once, rather than at each call site: a card that decides for
    // itself whether to render the guard loses the badge, the announcement and the reveal control all at
    // the same time, and the next card to be written forgets one of them.
    const masked = !isViewerOwner && (Boolean(isSpoiler) || maskedBySensitivePreference) && !revealed;
    const revealContent = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setRevealed(true);
    };
    // Whether the scrim yields the click to the link overlay beneath it. Keyed on the stacking the
    // CALLER asked for, never on whether a reveal button happens to exist — that coupling is what made
    // every masked search row unreachable for logged-out visitors between 2026-04-21 and 2026-08-02.
    // See the design doc's §8 (2026-07-26-sensitive-masking-precedence-and-persisted-gate-unlock-design.md).
    // Since D6 the reveal button exists on every masked variant, so the separated control layer always
    // carries it; the two flags stay distinct because §8.3's split (z-order vs floating control) is the
    // fix that keeps this composition honest.
    const yieldsToLinkOverlay = masked
        && overlayStacking === "below-link-overlay"
        && (variant === "avatar" || variant === "card");
    const showSeparatedRevealButton = yieldsToLinkOverlay;
    // The content-preference route, D6-demoted from the HIDE branch's substitute control to a compact
    // secondary link under the reveal button. Quiet on purpose: the reveal is the action the viewer
    // came for, the route is the one that changes tomorrow's default. It keeps the coarse-pointer
    // height floor so it stays a real tap target where the compact size would not be.
    const renderCardPreferencesRoute = (className: string) => (
        <a
            href={CONTENT_PREFERENCES_PATH}
            data-playwright-id="sensitive-guard-preferences-route"
            className={cn(
                "inline-flex max-w-full items-center justify-center gap-1.5 rounded-full px-2 py-1 text-center text-xs font-medium leading-tight text-muted-foreground underline underline-offset-2 transition hover:text-foreground focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
                "touch-manipulation [@media(pointer:coarse)]:min-h-11",
                className,
            )}
        >
            <SlidersHorizontal className="size-3.5 shrink-0" aria-hidden={true} />
            <span className="whitespace-normal">{preferencesRouteText}</span>
        </a>
    );
    const renderRevealButton = (className: string) => (
        <button
            type="button"
            data-playwright-id={revealButtonDataPlaywrightId}
            className={cn(
                // Inline-text variant: `px-3 py-1.5 text-sm` renders ~34px tall. In normal flow inside
                // an absolutely-positioned overlay, so the min-size floor grows only the button and
                // reflows no page content. Coarse pointer only — a mouse keeps the compact size.
                // Flat-X (rule 1): the border carries the separation, not a drop-shadow — and the one
                // sanctioned focus ring (rule 4), not a bespoke 2px primary-tinted one.
                "inline-flex items-center justify-center gap-2 rounded-full border border-border/60 bg-background1/96 text-foreground transition hover:bg-background2/92 focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
                "touch-manipulation [@media(pointer:coarse)]:min-h-11",
                className,
            )}
            onClick={revealContent}
        >
            <Eye className="size-4 shrink-0" aria-hidden={true} />
            <span className="whitespace-normal">{revealText}</span>
        </button>
    );
    const renderCardRevealButton = (className: string) => (
        <button
            type="button"
            data-playwright-id={revealButtonDataPlaywrightId}
            className={cn(
                // Card variant: ~36px tall (`px-4 py-2` + leading-tight). Same reasoning as the
                // inline-text variant — full-width inside the overlay, so height-only growth on touch.
                // No `shadow-md`: this pill sits INSIDE the overlay panel, which carries its own
                // elevation, and the pair read on the light capture as a soft neumorphic double
                // plinth floating on a member row — the only drop-shadow on the screen (rule 1).
                "inline-flex max-w-full items-center justify-center gap-2 rounded-full border border-border/70 bg-background1/96 px-4 py-2 text-center text-sm font-medium leading-tight text-foreground backdrop-blur-sm transition hover:bg-background2/92 focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
                "touch-manipulation [@media(pointer:coarse)]:min-h-11",
                className,
            )}
            onClick={revealContent}
        >
            <Eye className="size-4 shrink-0" aria-hidden={true} />
            <span className="whitespace-normal">{revealText}</span>
        </button>
    );
    const renderAvatarRevealButton = (className: string) => (
        <button
            type="button"
            data-playwright-id={revealButtonDataPlaywrightId}
            aria-label={typeof revealText === "string" ? revealText : undefined}
            title={typeof revealText === "string" ? revealText : undefined}
            className={cn(
                // Worst offender in the tap-target audit at 24x24. It CANNOT take a min-size floor:
                // it is centred inside a `size-6` avatar overlay and a 44px box would bleed out over
                // whatever sits next to the avatar. `touch-hit-44` gives it the 44px hit area with
                // the visual circle untouched.
                //
                // Two containment facts that make this work, both load-bearing:
                //  - this button is statically positioned, so it needs an explicit `relative` to be
                //    the pseudo-element's containing block (the utility sets no `position` on
                //    purpose — see src/css/03-tap-targets.css);
                //  - the guard root is `overflow-hidden` while masked, so the expander can never
                //    exceed the avatar's own box. The SMALLEST circle any `variant="avatar"` consumer
                //    gives the guard is `size-12` (48px), so 44px fits along both axes; a consumer
                //    smaller than 44px would silently clip the hit area back down. The five consumers
                //    and their sizes, corrected 2026-08-23 (`GROUP-16` — the old list named three of
                //    them and asserted a single 48px, which was already wrong when it was written):
                //    AddDiscussionToGroupDialog and AddDiscussionToSeriesDialog `size-12 sm:size-24`,
                //    GroupItemDialog `size-12`, GroupCardDisplayer `size-14 sm:size-16`,
                //    UserCardDisplayer `size-15 sm:size-16`.
                //
                //    That range, 48px to 96px, is why the overlay this control sits in carries NO
                //    words: a letterspaced badge measures ~54.5 CSS px of glyphs before its own
                //    padding, so at the low end of the range the round crop sheared it at both ends
                //    and the row read as a broken image (`DISCUSSION-33`, `GROUP-16`).
                "relative touch-hit-44",
                "inline-flex size-6 items-center justify-center rounded-full border border-border/60 bg-background1/96 text-foreground transition hover:bg-background2/92 focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
                className,
            )}
            onClick={revealContent}
        >
            <Eye className="size-3" aria-hidden={true} />
            <span className="sr-only">{revealText}</span>
        </button>
    );
    // The avatar mask's only visible content: the reveal control — never a word. See the sizing note
    // in `renderAvatarRevealButton` for why: the circle is 48-96px and the badge needs ~82px, so the
    // badge could not fit at the sizes this actually renders at. An avatar circle holds exactly one
    // control, and since D6 the primary one is always the reveal (the HIDE preferences route that used
    // to substitute for it lives on the card panel only).
    const avatarOverlayControl = !showSeparatedRevealButton
        ? renderAvatarRevealButton("size-6")
        : null;
    const overlayContent = variant === "avatar" ? (
        // `max-h-full max-w-full` so the plate can never be the thing the root has to crop, and no
        // `shadow-lg`: flat-X separates with a border (rule 1), and the shadow rendered as a soft
        // square-cornered plate bleeding past the circle, which is what stopped the avatar reading as
        // round (`GROUP-16`).
        <div className="flex max-h-full max-w-full items-center justify-center rounded-full border border-border/60 bg-popover/96 p-0.5 backdrop-blur-sm">
            <span className="sr-only">
                {label}
                {helperText ? <> {helperText}</> : null}
            </span>
            {avatarOverlayControl}
        </div>
    ) : isInlineCompact ? (
        <button
            type="button"
            data-playwright-id={revealButtonDataPlaywrightId}
            aria-label={typeof revealText === "string" ? revealText : undefined}
            className={cn(
                "flex h-full min-h-[4.5rem] w-full items-center justify-center rounded-2xl border border-border/70 bg-background1/90 px-3 py-2 text-center shadow-[inset_0_1px_0_rgba(255,255,255,0.08),inset_0_-18px_36px_rgba(0,0,0,0.2),0_12px_28px_rgba(0,0,0,0.14)] backdrop-blur-md",
                "bg-[repeating-linear-gradient(135deg,rgba(0,0,0,0.04)_0,rgba(0,0,0,0.04)_12px,rgba(0,0,0,0.08)_12px,rgba(0,0,0,0.08)_24px)]",
                "dark:bg-[repeating-linear-gradient(135deg,rgba(255,255,255,0.03)_0,rgba(255,255,255,0.03)_12px,rgba(255,255,255,0.08)_12px,rgba(255,255,255,0.08)_24px)]",
                "transition hover:bg-background1/96 focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
            )}
            onClick={revealContent}
        >
            <div className="pointer-events-none flex max-w-full items-center gap-2">
                <Eye className="size-4 shrink-0 text-foreground/80" aria-hidden={true} />
                <SensitiveBadge className="max-w-full truncate rounded-full border border-border/60 px-3 py-1 text-sm font-medium shadow-md backdrop-blur-sm">
                    {badgeLabel}
                </SensitiveBadge>
            </div>
        </button>
    ) : isCardActionOverlay ? (
        <div
            className={cn(
                // Elevation through the TOKEN, never a literal rgba (rule 1 + the token table). The
                // literal was theme-blind: it painted a soft plinth on a resting card in light, where
                // `--surface-shadow-soft` is deliberately `none`. The token is `none` in light and a
                // real shadow in dark, so both themes now get what the system says they should.
                // `bg-popover`, not `bg-background1`: in dark the panel fill and the blurred backdrop
                // behind it were within one 8-bit step of each other, so only a 1px border said a panel
                // was there at all (`GROUP-7`). `--popover` is background3-weighted in dark and
                // background1-weighted in light, so it separates from the page in BOTH themes.
                "max-w-[18rem] rounded-2xl border border-border/70 bg-popover/94 px-3 py-3 shadow-[var(--surface-shadow-soft)] backdrop-blur-md",
            )}
        >
            <div className="flex flex-col items-center gap-2 text-center">
                {/* The panel's visible announcement is the reveal button's own label ("Reveal
                    sensitive content"), on every preference — which is what keeps `GROUP-1` and
                    `DISCUSSION-19` closed now that D6 removed the HIDE-only visible badge + label
                    line: a masked card never renders a panel whose entire contents are sr-only,
                    because the button below always carries visible text. */}
                <span className="sr-only">
                    {label}
                    {helperText ? <> {helperText}</> : null}
                </span>
                {renderCardRevealButton("w-full")}
                {/* The route rides along only when the mask came from the viewer's own HIDE
                    preference: a BLUR viewer already sees blurs by choice, and a spoiler mask has
                    nothing to manage on that screen. Secondary by design — see the renderer. */}
                {hidePreferenceMask && preferencesRouteText
                    ? renderCardPreferencesRoute("w-full")
                    : null}
            </div>
        </div>
    ) : (
        <div
            className={cn(
                "max-w-[19rem] rounded-2xl border border-border/60 bg-popover/95 px-4 py-3 shadow-lg backdrop-blur-sm",
            )}
        >
            <div className="flex flex-col items-center gap-2 text-center">
                <SensitiveBadge>{badgeLabel}</SensitiveBadge>
                <div className="text-sm font-medium text-foreground">{label}</div>
                {helperText ? (
                    <div className="text-xs leading-5 text-muted-foreground">{helperText}</div>
                ) : null}
                {renderRevealButton("px-3 py-1.5 text-sm font-medium")}
            </div>
        </div>
    );

    // What the (now non-blocking) visual layer still carries once the reveal control has floated out of
    // it. The overlayContent branches are where the label and helper text live, so dropping them
    // wholesale would silently remove the only announcement of the masking for screen readers — for
    // the card AND the avatar variant alike (§8.3's first load-bearing detail).
    const separatedVisualContent = (
        <span className="sr-only">
            {label}
            {helperText ? <> {helperText}</> : null}
        </span>
    );

    return (
        <div
            className={cn(
                "relative",
                masked && "overflow-hidden",
                masked && isInlineCompact && "min-h-[4.5rem]",
                // Same idiom as the inline floor above, for the same reason one layer up: the overlay
                // is centred `absolute inset-0` inside a root that a text-only feed card leaves ~91px
                // tall, and a panel taller than the root shears its own explanation top and bottom
                // (`GROUP-16`).
                //
                // Two values, because the two branches hold different content and a floor sized for
                // the taller one would make every blurred card that tall: the ordinary panel is the
                // reveal button alone (~94px with the p-4 container), while a HIDE-preference panel
                // stacks the compact route link under it (~126px, and ~154px on a coarse pointer where
                // both controls carry the 44px floor — hence the coarse step). Safe against the home
                // feed's layout-shift reserve: `ssr_home_first_card.jte` records that HomeController
                // skips SSR for spoiler/sensitive/archived/deleted first cards, so a masked card is
                // never the card that reserve is emitted for.
                masked && isCardActionOverlay && (hidePreferenceMask
                    ? "min-h-32 [@media(pointer:coarse)]:min-h-40"
                    : "min-h-28"),
                className,
            )}
            data-sensitive-guard={masked ? "masked" : "visible"}
            data-sensitive-guard-mode={mode}
            data-sensitive-guard-variant={variant}
        >
            <div
                className={cn(
                    "transition duration-200 ease-out",
                    masked && ["pointer-events-none select-none", variantContentClassName[variant]],
                    masked && isInlineCompact && "min-h-[4.5rem]",
                    masked && maskedContentClassName,
                    contentClassName,
                )}
                aria-hidden={masked ? "true" : undefined}
            >
                {children}
            </div>

            {masked && (
                <div
                    data-sensitive-overlay-layer="visual"
                    className={cn(
                        "absolute inset-0",
                        // The wash is theme-aware for the same reason the brightness is (`GROUP-7`):
                        // washing near-black content in more near-black removes the last cue that
                        // anything is there. Dark uses a LIGHTENING scrim, exactly as the inline
                        // variant beside it already did.
                        isInlineCompact
                            ? "bg-black/12 backdrop-blur-[2px] dark:bg-white/8"
                            : isCardActionOverlay
                                ? "bg-background1/74 backdrop-blur-[3px] dark:bg-white/10"
                                : "bg-background1/70 backdrop-blur-[2px] dark:bg-white/10",
                        yieldsToLinkOverlay
                            ? "z-layer-base pointer-events-none"
                            : "z-layer-raised-front",
                        overlayClassName,
                    )}
                >
                    <div
                        className={cn(
                            isInlineCompact
                                ? "flex h-full w-full items-stretch justify-stretch p-2.5 text-left"
                                : isCardActionOverlay
                                    ? "flex h-full w-full items-center justify-center p-4 text-center"
                                    // `p-1`, matching the control layer below. `p-3` took 24px out of
                                    // the diameter of a circle that is 48px at its smallest, so the
                                    // two layers disagreed about how much of the avatar was usable.
                                    : variant === "avatar"
                                        ? "flex h-full w-full items-center justify-center p-1 text-center"
                                : "flex h-full w-full items-center justify-center p-3 text-center",
                            maskedContainerClassName,
                        )}
                    >
                        {showSeparatedRevealButton ? separatedVisualContent : overlayContent}
                    </div>
                </div>
            )}

            {showSeparatedRevealButton && (
                <div
                    data-sensitive-overlay-layer="control"
                    className={cn(
                        "pointer-events-none absolute z-layer-raised-front flex",
                        variant === "avatar"
                            ? "inset-0 items-center justify-center p-1"
                            : "inset-0 items-center justify-center p-3 text-center",
                    )}
                >
                    {variant === "avatar"
                        ? renderAvatarRevealButton("pointer-events-auto size-6")
                        : renderCardRevealButton("pointer-events-auto max-w-[18rem]")}
                </div>
            )}
        </div>
    );
}
